import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { ApplicantDiscussion } from '@app/models/recruiting/applicant-discussion.model';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { DiscussionComment } from '@app/models/recruiting/discussion-comment.model';
import { AnalyticService, NotifyService } from '@app/services';
import { HiringAccessLevelIdsEnum } from '../../enums/hiring-access-levels.enum';
import { ApplicantProfileService } from '../../services';

/**
 * Comments card component
 *
 * @requires Applicant
 */
@Component({
    selector: 'app-comments-card[applicant]',
    templateUrl: './comments-card.template.html',
})
export class CommentsCardComponent implements OnInit {
    /**
     * We can say safeuly use the not-null assertion operator here because
     * the selector requires the applicant input to be present.
     */
    @Input()
    applicant!: Applicant;

    applicantDiscussions: ApplicantDiscussion[] = [];
    selectedComment: DiscussionComment | null = null;
    hiringTeam: Employee[] = [];
    isLoading = true;

    isDeleteCommentDialogOpen = false;

    constructor(
        private notify: NotifyService,
        private route: ActivatedRoute,
        private analyticService: AnalyticService,
        private applicantProfileService: ApplicantProfileService
    ) {}

    async ngOnInit(): Promise<void> {
        this.hiringTeam = this.applicant.jobPosting.managerAssignments.map(
            (managerAssignment) => managerAssignment.employee
        );

        await this.fetchAndAssignDiscussions();

        this.scrollToCommentAndMarkItAsSelected();
        this.isLoading = false;
    }

    async onSaveComment(comment: DiscussionComment, applicantDiscussion: ApplicantDiscussion): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantDiscussionComment);
        this.analyticService.trackEvent(AnalyticEvents.RecruitingApplicantProfileCommentsTab);
        this.isLoading = true;

        try {
            const notificationVerb = comment.id ? 'updated' : 'posted';
            comment.atsApplicantDiscussionId = applicantDiscussion.id;

            comment.isPrivate = comment.accessLevelId === HiringAccessLevelIdsEnum.private;

            await comment.save();
            this.applicantProfileService.clearApplicantDiscussion();
            await this.fetchAndAssignDiscussions();
            this.notify.success({
                key: 'applicant-tracker.yourCommentHasBeenActionedSuccessfully',
                params: { action: notificationVerb },
            });
        } catch (e) {
            this.notify.error(e);
        }

        this.isLoading = false;
    }

    async onDeleteComment(): Promise<void> {
        if (!this.selectedComment) {
            throw new Error('No comment selected');
        }
        this.isLoading = true;

        try {
            await this.selectedComment.delete();
            this.applicantProfileService.clearApplicantDiscussion();
            await this.fetchAndAssignDiscussions();
            this.notify.success('applicant-tracker.commentHasBeenSuccessfullyDeleted');
        } catch (e) {
            this.notify.error(e);
        }
        this.closeDeleteCommentDialogOpen();
        this.isLoading = false;
    }

    closeDeleteCommentDialogOpen(): void {
        this.isDeleteCommentDialogOpen = false;
    }

    openDeleteCommentDialogOpen(comment: DiscussionComment): void {
        this.isDeleteCommentDialogOpen = true;

        this.selectedComment = comment;
    }

    private async fetchAndAssignDiscussions(): Promise<void> {
        try {
            this.applicantDiscussions = await this.applicantProfileService.getDiscussions(this.applicant);
        } catch (e) {
            this.notify.error(e);
        }
    }

    private scrollToCommentAndMarkItAsSelected(): void {
        this.route.fragment.subscribe((fragment) => {
            if (!fragment) {
                return;
            }

            setTimeout(() => {
                const commentElement = document.getElementById(fragment);

                if (!commentElement) {
                    return;
                }

                const outletEl = document.querySelector('.ui.outlet');
                if (outletEl) {
                    outletEl.scrollTop = commentElement.offsetTop;
                }

                const commentId = +fragment.split('-')[1];
                const selectedDiscussion = this.applicantDiscussions.find((ad) =>
                    ad.discussionComments.map((c) => c.id).includes(commentId)
                );
                if (!selectedDiscussion) {
                    return;
                }

                const selectedComment = selectedDiscussion.discussionComments.find((c) => c.id === commentId);
                if (!selectedComment) {
                    return;
                }

                selectedComment.selected = true;

                // Highlight stay for 10 seconds
                setTimeout(() => (selectedComment.selected = false), 10000);
            });
        });
    }
}
